import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, setAdminToken, getAdminToken, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { EVENT5K } from "@/constants/testIds";
import { toast } from "sonner";
import { Download, LogOut, ShieldCheck, Flame, Plus, RotateCcw, Users, Send } from "lucide-react";
import VendorsAdminPanel from "./VendorsAdminPanel";

const TABS = [
  { key: "registrations", label: "Registrations", testId: EVENT5K.adminTabRegistrations },
  { key: "vendors", label: "Vendors", testId: EVENT5K.adminTabVendors },
  { key: "sponsors", label: "Sponsors", testId: EVENT5K.adminTabSponsors },
  { key: "volunteers", label: "Volunteers", testId: EVENT5K.adminTabVolunteers },
  { key: "donations", label: "Donations", testId: EVENT5K.adminTabDonations },
];

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [tab, setTab] = useState("registrations");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, goal: 5000, percent: 0 });
  const [adjAmount, setAdjAmount] = useState("");
  const [adjLabel, setAdjLabel] = useState("");

  const loadProgress = async () => {
    try {
      const { data } = await api.get("/fundraising/progress");
      setProgress(data);
    } catch {
      // silent
    }
  };

  const addAdjustment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(adjAmount);
    if (isNaN(amount) || amount === 0) {
      toast.error("Enter a non-zero amount (use negative to subtract).");
      return;
    }
    try {
      await api.post("/admin/adjustments", { amount, label: adjLabel || "", source: "manual" });
      toast.success(`Added $${amount.toFixed(2)} to the thermometer.`);
      setAdjAmount("");
      setAdjLabel("");
      loadProgress();
    } catch {
      toast.error("Could not add adjustment.");
    }
  };

  const resetThermometer = async () => {
    if (!window.confirm("Reset the fundraising thermometer to $0? This deletes ALL prior adjustments and cannot be undone.")) return;
    try {
      const { data } = await api.delete("/admin/adjustments/all");
      toast.success(`Thermometer reset — cleared ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"}.`);
      loadProgress();
    } catch {
      toast.error("Could not reset thermometer.");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { password });
      setAdminToken(data.token);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid password.");
    }
  };

  const logout = () => {
    setAdminToken(null);
    setAuthed(false);
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  const loadTab = async (t) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${t}`);
      setRows(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) {
      loadStats();
      loadProgress();
      loadTab(tab);
    }
    // eslint-disable-next-line
  }, [authed, tab]);

  const exportCsv = () => {
    const token = getAdminToken();
    window.open(`${API}/admin/${tab}/export?_=${Date.now()}`, "_blank"); // no-op if unauth
    // Simple approach: fetch with header then blob
    fetch(`${API}/admin/${tab}/export`, { headers: { "X-Admin-Token": token } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${tab}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not export."));
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-md bg-[#141414] border border-[#27272A] p-8">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-6 h-6 text-[#00A651]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00A651] font-bold">Admin</div>
              <div className="font-display text-2xl uppercase text-white">Run to Empower · Dashboard</div>
            </div>
          </div>
          <label className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]">Password</label>
          <Input
            data-testid={EVENT5K.adminPassword}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 bg-black border-[#27272A]"
            required
          />
          <button
            data-testid={EVENT5K.adminLoginBtn}
            className="mt-6 w-full bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-sm py-3"
          >
            Sign In
          </button>
          <Link to="/" className="mt-4 block text-center text-xs uppercase tracking-widest text-[#71717A] hover:text-white">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  const columns = COLUMN_MAP[tab] || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#27272A] bg-[#0A0A0A] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">Admin</div>
              <div className="font-display text-lg uppercase text-white">Run to Empower Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/admin/contacts"
              data-testid="admin-nav-contacts"
              className="inline-flex items-center gap-2 border border-white/30 text-white hover:border-[#00A651] hover:text-[#00A651] font-bold uppercase tracking-widest text-xs px-4 py-2"
            >
              <Users className="w-4 h-4" /> Contacts
            </Link>
            <Link
              to="/admin/campaigns"
              data-testid="admin-nav-campaigns"
              className="inline-flex items-center gap-2 border border-white/30 text-white hover:border-[#EC008C] hover:text-[#EC008C] font-bold uppercase tracking-widest text-xs px-4 py-2"
            >
              <Send className="w-4 h-4" /> Campaigns
            </Link>
            <button
              onClick={exportCsv}
              data-testid={EVENT5K.adminExportBtn}
              className="hidden sm:inline-flex items-center gap-2 border-2 border-[#EC008C] text-[#EC008C] hover:bg-[#EC008C] hover:text-white font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid={EVENT5K.adminLogoutBtn}
              className="inline-flex items-center gap-2 border border-white/30 text-white hover:border-[#00A651] hover:text-[#00A651] font-bold uppercase tracking-widest text-xs px-4 py-2"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Fundraising thermometer control panel */}
        <div className="bg-[#141414] border border-[#00A651]/40 p-5 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-[#00A651] flex items-center justify-center">
                <Flame className="w-5 h-5 text-black" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">Fundraising Thermometer</div>
                <div className="font-display text-2xl uppercase text-white leading-none mt-1">
                  <span data-testid="admin-thermo-current">${Number(progress.current || 0).toLocaleString()}</span>
                  <span className="text-white/40 mx-1">/</span>
                  <span>${Number(progress.goal || 0).toLocaleString()}</span>
                  <span className="ml-3 text-[#EC008C] text-base">{Math.round(progress.percent || 0)}%</span>
                </div>
              </div>
            </div>
            <button
              onClick={resetThermometer}
              data-testid="admin-thermo-reset"
              className="inline-flex items-center gap-2 border-2 border-[#EC008C] text-[#EC008C] hover:bg-[#EC008C] hover:text-white font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Reset to $0
            </button>
          </div>
          <form onSubmit={addAdjustment} className="mt-5 grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-3">
            <Input
              data-testid="admin-thermo-amount"
              value={adjAmount}
              onChange={(e) => setAdjAmount(e.target.value)}
              placeholder="$ amount (e.g. 250)"
              type="number"
              step="0.01"
              className="bg-black border-[#27272A]"
            />
            <Input
              data-testid="admin-thermo-label"
              value={adjLabel}
              onChange={(e) => setAdjLabel(e.target.value)}
              placeholder="Label (optional — e.g. RunSignup week of 3/10)"
              className="bg-black border-[#27272A]"
            />
            <button
              type="submit"
              data-testid="admin-thermo-add"
              className="inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-xs px-6 py-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add to Total
            </button>
          </form>
          <p className="mt-3 text-[10px] uppercase tracking-widest text-[#71717A]">
            Enter a positive number to add donations to the thermometer, or a negative number to subtract.
          </p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            <Stat label="Registrations" value={stats.registrations} accent="#00A651" />
            <Stat label="Vendors" value={stats.vendors} accent="#EC008C" />
            <Stat label="Sponsors" value={stats.sponsors} accent="#00A651" />
            <Stat label="Volunteers" value={stats.volunteers} accent="#EC008C" />
            <Stat label="Donations" value={`$${(stats.donation_total || 0).toFixed(0)}`} accent="#00A651" />
          </div>
        )}

        <div className="flex flex-wrap gap-1 border-b border-[#27272A] mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={t.testId}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${
                tab === t.key
                  ? "text-white border-b-2 border-[#00A651]"
                  : "text-[#71717A] hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          {tab === "vendors" ? (
            <VendorsAdminPanel />
          ) : loading ? (
            <div className="bg-[#141414] border border-[#27272A] p-10 text-center text-[#71717A] uppercase tracking-widest text-xs">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="bg-[#141414] border border-[#27272A] p-10 text-center text-[#71717A] uppercase tracking-widest text-xs">No records yet</div>
          ) : (
            <table className="w-full text-sm bg-[#141414] border border-[#27272A]">
              <thead>
                <tr className="bg-black text-[#A3A3A3] uppercase tracking-widest text-[10px]">
                  {columns.map((c) => (
                    <th key={c.key} className="text-left px-4 py-3 font-semibold">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#27272A] hover:bg-[#1A1A1A]">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 align-top">
                        {c.render ? c.render(r) : String(r[c.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#141414] border border-[#27272A] p-4">
      <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: accent }}>{label}</div>
      <div className="mt-1 font-display text-3xl font-black text-white">{value}</div>
    </div>
  );
}

const dateCell = (r) => (r.created_at || "").slice(0, 16).replace("T", " ");

const COLUMN_MAP = {
  registrations: [
    { key: "created_at", label: "When", render: dateCell },
    { key: "name", label: "Name", render: (r) => `${r.first_name} ${r.last_name}` },
    { key: "email", label: "Email" },
    { key: "race_option", label: "Race", render: (r) => (r.race_option === "5k" ? "5K" : "1-Mile") },
    { key: "shirt", label: "Shirt", render: (r) => (r.with_shirt ? `Yes · ${r.shirt_size || ""}` : "No") },
    { key: "team_name", label: "Team" },
    { key: "tier_label", label: "Tier" },
    { key: "amount_due", label: "Amount", render: (r) => `$${Number(r.amount_due || 0).toFixed(2)}` },
    { key: "payment_status", label: "Status" },
  ],
  vendors: [
    { key: "created_at", label: "When", render: dateCell },
    { key: "business_name", label: "Business" },
    { key: "contact_name", label: "Contact" },
    { key: "email", label: "Email" },
    { key: "vendor_type", label: "Type" },
    { key: "needs_power", label: "Power", render: (r) => (r.needs_power ? "Yes" : "No") },
  ],
  sponsors: [
    { key: "created_at", label: "When", render: dateCell },
    { key: "company_name", label: "Company" },
    { key: "contact_name", label: "Contact" },
    { key: "email", label: "Email" },
    { key: "sponsorship_level", label: "Level" },
    { key: "interest", label: "Interest", render: (r) => r.interest || "—" },
  ],
  volunteers: [
    { key: "created_at", label: "When", render: dateCell },
    { key: "name", label: "Name", render: (r) => `${r.first_name} ${r.last_name}` },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "availability", label: "Roles", render: (r) => (r.availability || []).join(", ") },
  ],
  donations: [
    { key: "created_at", label: "When", render: dateCell },
    { key: "donor_name", label: "Donor" },
    { key: "email", label: "Email" },
    { key: "amount", label: "Amount", render: (r) => `$${Number(r.amount || 0).toFixed(2)}` },
    { key: "anonymous", label: "Anonymous", render: (r) => (r.anonymous ? "Yes" : "No") },
  ],
};
